import { findRow, readTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { binPart, binsOfRack, shapeFromPositions } from './rackShape'
import { ropRows } from './wms.handler'

/**
 * Agregasi baca-saja INV-01 (Stok Inventory): stok RM per Material / per Rack, Kartu Stok Bulanan, dan stok FG
 * Zone → Pallet → unit beserta denah. Tidak ada tabel baru: semuanya dihitung dari `rm_unit`, `stock_movement`,
 * `fg_label`, `pallet`, `container`, `fg_pack_stock`. v7.50: tidak ada stok awal tanpa label — kemasan Pendataan Awal
 * baru menjadi stok setelah opname pertama disetujui (sebelum itu `AWAITING_FIRST_OPNAME`, tidak tampil).
 */

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')
const num = (value: unknown) => Number(value ?? 0)
const low = (value: unknown) => String(value ?? '').toLowerCase()
const round1 = (value: number) => Math.round(value * 10) / 10
const round2 = (value: number) => Math.round(value * 100) / 100

export const PAL_CAP = 24
export const PAL_REC = 18

/** Sembilan status unit RM → tiga kelompok (BA 12F INV-01). */
export const STOCK_GROUPS = {
  stok: ['IN_STOCK'],
  proses: ['AWAITING_PUTAWAY', 'IN_TRANSFER_STAGING', 'IN_TRANSIT', 'PICKED']
} as const
export type StockGroup = 'stok' | 'proses' | 'keluar'
export const groupOfStatus = (status: unknown): StockGroup =>
  (STOCK_GROUPS.stok as readonly string[]).includes(String(status))
    ? 'stok'
    : (STOCK_GROUPS.proses as readonly string[]).includes(String(status))
      ? 'proses'
      : 'keluar'

type Params = Record<string, unknown> | undefined
const text = (params: Params, key: string) => String(params?.[key] ?? '')
/** Gudang dalam cakupan role pemanggil (`scope_whs`, diisi endpoint INV-01); tanpa nilai = semua gudang. */
const inScopeWh = (params: Params) => {
  const whs = params?.scope_whs as string[] | undefined
  return (code: string) => !whs || whs.includes(code)
}

/* ============================================================ master ringkas */

const materials = () => readTable('mt_material').filter(material => material.is_active !== false)
const categoryName = (material: LocalRow | undefined) =>
  String(findRow('mt_material_group', idOf(material?.category_id))?.name ?? '')
const unitName = (id: unknown) => String(findRow('mt_unit', idOf(id))?.name ?? '')
const warehouseCodeOfZone = (zoneId: unknown) => {
  const zone = findRow('warehouse_zone', idOf(zoneId))
  return String(findRow('mt_warehouses', idOf(zone?.warehouse_id))?.code ?? 'WH-RM')
}
const binCode = (id: unknown) => String(findRow('mt_rack_bin', idOf(id))?.code ?? '')
const perBigOf = (unit: LocalRow, material: LocalRow | undefined) =>
  Math.max(1, num(unit.small_per_big_snapshot) || num(material?.default_purchase_unit_qty) || 1)

/** `ETK-BOLD20:20260801:01:0001` → `ETK-BOLD20-260801-01` */
export const batchOfQr = (qr: unknown) => {
  const part = String(qr ?? '').split(':')
  return part.length >= 3 ? `${part[0]}-${String(part[1]).slice(2)}-${part[2]}` : ''
}

/* ============================================================ INV-01 · Raw Material */

/** Kemasan Pendataan Awal yang belum disetujui atau stikernya dibatalkan bukan stok (prototype `stokRm` v7.45). */
const NOT_STOCK_YET = new Set(['AWAITING_FIRST_OPNAME', 'CANCELLED_OPENING'])

type RmUnitRow = {
  key: string
  kind: 'UNIT'
  unit_id: string | null
  qr_code: string | null
  status: string
  group: StockGroup
  rack_bin_code: string | null
  warehouse_code: string
  material_id: string
  qty_small: number
  qty_big: number
  unit_big: string
  batch_no: string
  stored_at: string | null
  flag: string
  is_wip: boolean
  override_reason: string | null
  place_note: string | null
  zone_code: string | null
}

const zoneCode = (id: unknown) => String(findRow('warehouse_zone', idOf(id))?.code ?? '') || null

function collectRmRows(): RmUnitRow[] {
  const mats = new Map(readTable('mt_material').map(material => [material.id as string, material]))
  const overrides = new Map(
    readTable('rm_putaway_task_item')
      .filter(item => item.override_reason)
      .map(item => [idOf(item.unit_id), String(item.override_reason)])
  )
  return readTable('rm_unit')
    .filter(unit => !NOT_STOCK_YET.has(String(unit.unit_status)))
    .map(unit => {
      const material = mats.get(idOf(unit.material_id))
      const perBig = perBigOf(unit, material)
      return {
        key: String(unit.id),
        kind: 'UNIT' as const,
        unit_id: String(unit.id),
        qr_code: String(unit.qr_code ?? ''),
        status: String(unit.unit_status),
        group: groupOfStatus(unit.unit_status),
        rack_bin_code: binCode(unit.rack_bin_id) || null,
        warehouse_code: warehouseCodeOfZone(unit.warehouse_zone_id),
        material_id: idOf(unit.material_id),
        qty_small: num(unit.qty_small),
        qty_big: round2(num(unit.qty_small) / perBig),
        unit_big: String(unit.unit_big_snapshot ?? unitName(material?.default_purchase_unit_id)),
        batch_no: batchOfQr(unit.qr_code),
        stored_at: (unit.stored_at as string | null) ?? null,
        flag: String(unit.flag ?? 'REGULAR'),
        is_wip: Boolean(unit.is_leftover_wip) || unit.flag === 'WIP',
        override_reason: overrides.get(String(unit.id)) ?? null,
        place_note: null,
        zone_code: zoneCode(unit.warehouse_zone_id)
      }
    })
}

export function stockRm(params: Params) {
  const q = low(text(params, 'q')).trim()
  const wh = text(params, 'wh')
  const kategori = text(params, 'kategori')
  const kelompok = (text(params, 'kelompok') || 'stok') as StockGroup | 'all'
  const detail = text(params, 'status')
  const onlyWip = text(params, 'wip') === '1'
  const onlyOvr = text(params, 'ovr') === '1'

  const mats = materials()
  const matById = new Map(mats.map(material => [material.id as string, material]))
  const rop = new Map(ropRows().map(row => [row.material_id as string, row]))
  const scoped = inScopeWh(params)
  const rows = collectRmRows().filter(row => matById.has(row.material_id) && scoped(row.warehouse_code))

  const matches = (row: RmUnitRow) => {
    const material = matById.get(row.material_id)
    if (wh && row.warehouse_code !== wh) return false
    if (kategori && idOf(material?.category_id) !== kategori) return false
    if (detail && row.status !== detail) return false
    if (onlyWip && !row.is_wip) return false
    if (onlyOvr && !row.override_reason) return false
    if (!q) return true
    const materialHit = low(`${material?.code} ${material?.name}`).includes(q)
    return (
      materialHit ||
      [row.qr_code, row.batch_no, row.rack_bin_code, row.place_note].some(value => low(value).includes(q))
    )
  }
  const filtered = rows.filter(matches)
  const counts = {
    stok: filtered.filter(row => row.group === 'stok').length,
    proses: filtered.filter(row => row.group === 'proses').length,
    keluar: filtered.filter(row => row.group === 'keluar').length
  }
  const shown = filtered.filter(row => kelompok === 'all' || row.group === kelompok)
  const narrowed = Boolean(q || wh || kategori || detail || onlyWip || onlyOvr)

  const materialRows = mats
    .map(material => {
      const mine = rows.filter(row => row.material_id === material.id && matches(row))
      const inRack = mine.filter(row => row.group === 'stok')
      const inFlight = mine.filter(row => row.group === 'proses')
      const saldoSmall = inRack.reduce((sum, row) => sum + row.qty_small, 0)
      const saldoBig = inRack.reduce((sum, row) => sum + row.qty_big, 0)
      const ropRow = rop.get(material.id as string)
      const ropLimit = ropRow?.rop_limit ?? null
      const capDay = num(material.cap_day)
      const locations = [
        ...new Set(inRack.map(row => row.rack_bin_code).filter((code): code is string => Boolean(code)))
      ]
      const perBigSet = new Set(mine.map(row => row.qty_small / (row.qty_big || 1)))
      const listed = mine.filter(row => kelompok === 'all' || row.group === kelompok)
      return {
        material_id: material.id as string,
        code: String(material.code),
        name: String(material.name),
        category: categoryName(material),
        unit_small: unitName(material.unit_small_id),
        unit_big: unitName(material.default_purchase_unit_id),
        saldo_small: round2(saldoSmall),
        saldo_big: round2(saldoBig),
        in_process_small: round2(inFlight.reduce((sum, row) => sum + row.qty_small, 0)),
        cap_day: capDay || null,
        days_of_cover: capDay ? round1(saldoSmall / capDay) : null,
        lead_days: ropRow?.lead_days_longest ?? null,
        rop_limit: ropLimit,
        status_stok: ropLimit == null ? '-' : saldoSmall <= ropLimit ? 'PESAN' : 'AMAN',
        locations,
        unit_count: listed.length,
        mixed_pack: perBigSet.size > 1,
        units: listed.sort((a, b) => String(a.rack_bin_code).localeCompare(String(b.rack_bin_code)))
      }
    })
    .filter(row => !narrowed || row.units.length > 0)

  const racks = new Map<
    string,
    { rack_bin_code: string; warehouse_code: string; units: number; materials: Set<string> }
  >()
  shown
    .filter(row => row.group === 'stok' && row.rack_bin_code)
    .forEach(row => {
      const key = String(row.rack_bin_code)
      const entry = racks.get(key) ?? {
        rack_bin_code: key,
        warehouse_code: row.warehouse_code,
        units: 0,
        materials: new Set()
      }
      entry.units += 1
      entry.materials.add(String(matById.get(row.material_id)?.code))
      racks.set(key, entry)
    })
  const rackRows = [...racks.values()]
    .map(entry => ({
      rack_bin_code: entry.rack_bin_code,
      rack_code: entry.rack_bin_code.split('-')[0],
      warehouse_code: entry.warehouse_code,
      unit_count: entry.units,
      materials: [...entry.materials].sort(),
      is_single: entry.materials.size <= 1
    }))
    .sort((a, b) => a.rack_bin_code.localeCompare(b.rack_bin_code))

  /**
   * Denah per rack (prototype `rkData` + `denahRack`, PDF butir 3): bentuk tiap rack + isi tiap posisi dari unit yang
   * tampil (saringan & kelompok aktif). Posisi berisi > 1 material = campur (merah di gambar).
   */
  const byBin = new Map<string, RmUnitRow[]>()
  shown
    .filter(row => row.group !== 'keluar' && row.rack_bin_code)
    .forEach(row => byBin.set(String(row.rack_bin_code), [...(byBin.get(String(row.rack_bin_code)) ?? []), row]))
  const dayMs = 86_400_000
  const rackMaps = readTable('mt_rack')
    .filter(rack => rack.is_active !== false)
    .map(rack => {
      const zone = findRow('warehouse_zone', idOf(rack.zone_id))
      const positions = binsOfRack(String(rack.id))
        .map(bin => ({
          code: String(bin.code),
          b: binPart(bin, 'column'),
          l: binPart(bin, 'level'),
          p: binPart(bin, 'position'),
          active: bin.is_active !== false
        }))
        .sort((a, b) => a.b - b.b || a.l - b.l || a.p - b.p)
      return {
        rack_code: String(rack.code),
        name: (rack.name as string | null) ?? null,
        warehouse_code: String(findRow('mt_warehouses', idOf(zone?.warehouse_id))?.code ?? 'WH-RM'),
        category: rack.category_restriction_id
          ? String(findRow('mt_material_group', idOf(rack.category_restriction_id))?.name ?? '')
          : null,
        shape: shapeFromPositions(positions).shape,
        positions: positions.map(position => {
          const units = byBin.get(position.code) ?? []
          const codes = [...new Set(units.map(unit => String(matById.get(unit.material_id)?.code ?? '')))]
          return {
            ...position,
            materials: codes,
            category: units.length ? categoryName(matById.get(units[0].material_id)) : null,
            unit_count: units.length,
            unit_name: units[0]?.unit_big ?? 'unit',
            age_days: units.length
              ? Math.max(
                  ...units.map(unit =>
                    unit.stored_at
                      ? Math.max(0, Math.floor((Date.now() - new Date(unit.stored_at).getTime()) / dayMs))
                      : 0
                  )
                )
              : 0
          }
        })
      }
    })
    .filter(map => scoped(map.warehouse_code) && (!wh || map.warehouse_code === wh))
    .sort((a, b) => a.warehouse_code.localeCompare(b.warehouse_code) || a.rack_code.localeCompare(b.rack_code))

  /**
   * "Area Khusus dan Belum Ditempatkan" (v7.50 U-19, dulu "Di Luar Rak"): stok tanpa posisi rak — area khusus
   * (Karantina, Staging, Area QC) atau unit yang belum ditempatkan. Tidak digambar sebagai rack, tetapi tetap terlihat.
   */
  const areas = new Map<
    string,
    { location: string; kind: 'area' | 'unplaced'; warehouse_code: string; units: number; materials: Set<string> }
  >()
  shown
    .filter(row => row.group !== 'keluar' && !row.rack_bin_code)
    .forEach(row => {
      const location = row.zone_code ?? 'Belum ditempatkan'
      const entry = areas.get(location) ?? {
        location,
        kind: row.zone_code ? ('area' as const) : ('unplaced' as const),
        warehouse_code: row.warehouse_code,
        units: 0,
        materials: new Set<string>()
      }
      entry.units += 1
      entry.materials.add(String(matById.get(row.material_id)?.code))
      areas.set(location, entry)
    })
  const specialAreas = [...areas.values()]
    .map(entry => ({
      location: entry.location,
      kind: entry.kind,
      warehouse_code: entry.warehouse_code,
      unit_count: entry.units,
      materials: [...entry.materials].sort()
    }))
    .sort(
      (a, b) => Number(a.kind === 'unplaced') - Number(b.kind === 'unplaced') || a.location.localeCompare(b.location)
    )

  const tobacco = mats
    .filter(material => low(categoryName(material)) === 'tembakau')
    .map(material => ({
      code: String(material.code),
      unit: unitName(material.unit_small_id),
      qty: round2(
        rows
          .filter(row => row.material_id === material.id && row.group === 'stok')
          .reduce((sum, row) => sum + row.qty_small, 0)
      )
    }))
  const need = mats
    .map(material => ({ material, row: rop.get(material.id as string) }))
    .filter(({ material }) => {
      const mine = rows.filter(row => row.material_id === material.id && row.group === 'stok')
      const ropLimit = rop.get(material.id as string)?.rop_limit
      return ropLimit != null && mine.reduce((sum, row) => sum + row.qty_small, 0) <= ropLimit
    })
    .map(({ material }) => String(material.code))

  return {
    counts,
    tobacco,
    material_total: mats.length,
    need_order: { count: need.length, codes: need.slice(0, 5) },
    materials: materialRows,
    racks: rackRows,
    rack_maps: rackMaps,
    special_areas: specialAreas,
    multi_material_racks: rackRows.filter(row => !row.is_single).map(row => row.rack_bin_code),
    options: {
      warehouses: [...new Set(rows.map(row => row.warehouse_code))].sort(),
      categories: readTable('mt_material_group').map(group => ({ id: group.id as string, name: String(group.name) })),
      statuses: [...new Set(rows.map(row => row.status))].sort()
    }
  }
}

/* ============================================================ INV-01 · Kartu Stok Bulanan */

const monthOf = (params: Params) => {
  const raw = text(params, 'month') || new Date().toISOString().slice(0, 7)
  const [year, month] = raw.split('-').map(Number)
  return { year, month, key: `${year}-${String(month).padStart(2, '0')}`, days: new Date(year, month, 0).getDate() }
}
/** Hari (1…31) menurut jam Jakarta dari stempel ISO. */
const dayInMonth = (iso: unknown, key: string) => {
  const date = new Date(String(iso ?? ''))
  if (Number.isNaN(date.getTime())) return 0
  const local = new Date(date.getTime() + 7 * 3600_000).toISOString()
  return local.startsWith(key) ? Number(local.slice(8, 10)) : 0
}

export function stockCard(params: Params) {
  const { key, days, year, month } = monthOf(params)
  const q = low(text(params, 'q')).trim()
  const kategori = text(params, 'kategori')
  const mats = materials()
  const matById = new Map(mats.map(material => [material.id as string, material]))
  const units = new Map(readTable('rm_unit').map(unit => [unit.id as string, unit]))
  const inn = new Map<string, number[]>()
  const out = new Map<string, number[]>()
  const bump = (map: Map<string, number[]>, materialId: string, day: number, qty: number) => {
    if (!day) return
    const series = map.get(materialId) ?? Array.from({ length: days + 1 }, () => 0)
    series[day] += qty
    map.set(materialId, series)
  }
  /** Tembakau dihitung dalam satuan kecil (kg); material lain dalam satuan besar (BA 12F). */
  const toDisplay = (material: LocalRow | undefined, unit: LocalRow | undefined, qtySmall: number) =>
    low(categoryName(material)) === 'tembakau' ? qtySmall : qtySmall / perBigOf(unit ?? ({} as LocalRow), material)
  const scoped = inScopeWh(params)
  const inWh = (unit: LocalRow | undefined) => Boolean(unit) && scoped(warehouseCodeOfZone(unit?.warehouse_zone_id))
  units.forEach(unit => {
    const material = matById.get(idOf(unit.material_id))
    if (!material || !unit.stored_at || unit.flag === 'NG' || !inWh(unit)) return
    bump(inn, String(material.id), dayInMonth(unit.stored_at, key), toDisplay(material, unit, num(unit.qty_small)))
  })
  readTable('stock_movement')
    .filter(move => move.movement_type === 'RM_PICK')
    .forEach(move => {
      const unit = units.get(idOf(move.unit_id))
      const material = matById.get(idOf(unit?.material_id))
      if (!material || !inWh(unit)) return
      bump(out, String(material.id), dayInMonth(move.ts, key), toDisplay(material, unit, num(move.qty_small)))
    })
  const rows = mats
    .filter(material => !kategori || idOf(material.category_id) === kategori)
    .filter(material => !q || low(`${material.code} ${material.name}`).includes(q))
    .map(material => {
      const series = (map: Map<string, number[]>) =>
        (map.get(material.id as string) ?? Array.from({ length: days + 1 }, () => 0)).slice(1).map(round2)
      const incoming = series(inn)
      const outgoing = series(out)
      return {
        material_id: material.id as string,
        code: String(material.code),
        name: String(material.name),
        category: categoryName(material),
        unit:
          low(categoryName(material)) === 'tembakau'
            ? unitName(material.unit_small_id)
            : unitName(material.default_purchase_unit_id),
        incoming,
        outgoing,
        total_in: round2(incoming.reduce((sum, value) => sum + value, 0)),
        total_out: round2(outgoing.reduce((sum, value) => sum + value, 0))
      }
    })
  return { month: key, year, month_no: month, days, rows }
}

/* ============================================================ INV-01 · Finished Goods */

type FgProduct = { name: string; code: string; pack: Record<string, number> }

function productIndex() {
  const map = new Map<string, FgProduct>()
  readTable('mt_product').forEach(product => {
    const slop = num(product.pack_per_slop) || 10
    const bal = slop * (num(product.slop_per_bal) || 20)
    const karton = bal * (num(product.bal_per_karton) || 4)
    map.set(product.id as string, {
      name: String(product.name),
      code: String(product.code),
      pack: { Karton: karton, Bal: bal, Slop: slop, Pack: 1 }
    })
  })
  return map
}

const FG_PALLET_STATUSES = new Set(['IN_STOCK', 'ALLOCATED', 'CONSOLIDATION_RESERVED', 'MERGE_RESERVED'])
const LOCKED = new Set(['ALLOCATED', 'CONSOLIDATION_RESERVED', 'MERGE_RESERVED'])
/** Pita isi pallet; `rec` = rekomendasi pallet itu (Master Lokasi Gudang), bawaan 18 karton. */
export const bandOf = (karton: number, rec = PAL_REC) =>
  !(karton > 0)
    ? 'kosong'
    : karton < rec - 4
      ? 'longgar'
      : karton < rec
        ? 'hampir'
        : karton === rec
          ? 'rekomendasi'
          : 'berlebih'

export function stockFg(params: Params) {
  const q = low(text(params, 'q')).trim()
  const band = text(params, 'band')
  const products = productIndex()
  const batches = new Map(readTable('fg_labeler_batch').map(batch => [batch.id as string, String(batch.batch_no)]))
  const labels = readTable('fg_label').filter(label => FG_PALLET_STATUSES.has(String(label.label_status)))
  const returLabels = new Set(
    readTable('fg_label')
      .filter(label => label.is_return)
      .map(label => label.id as string)
  )

  const unitRow = (label: LocalRow) => {
    const product = products.get(idOf(label.sku_id))
    const level = String(label.level)
    return {
      label_no: String(label.label_no),
      level,
      batch_no: batches.get(idOf(label.batch_id)) ?? '',
      equivalent_pack: product?.pack[level] ?? 0,
      status: String(label.label_status),
      locked: LOCKED.has(String(label.label_status)),
      is_return: returLabels.has(label.id as string)
    }
  }

  const pallets = readTable('pallet')
    .filter(pallet => pallet.pallet_kind === 'FG')
    .map(pallet => {
      const zone = findRow('warehouse_zone', idOf(pallet.zone_id))
      const mine = labels.filter(label => idOf(label.pallet_id) === pallet.id)
      const sku = mine[0] ? products.get(idOf(mine[0].sku_id)) : undefined
      const level = String(pallet.unit_type ?? mine[0]?.level ?? 'Karton')
      const counted = mine.filter(label => String(label.level) === level)
      const eq = mine.reduce(
        (sum, label) => sum + (products.get(idOf(label.sku_id))?.pack[String(label.level)] ?? 0),
        0
      )
      const kartonPack = sku?.pack.Karton ?? 800
      const keq = round2(eq / kartonPack)
      const palCap = num(pallet.capacity) || PAL_CAP
      const palRec = num(pallet.recommended) || PAL_REC
      const capacity = level === 'Bal' ? palCap * (kartonPack / (sku?.pack.Bal ?? 200)) : palCap
      const fill = level === 'Bal' ? counted.length : keq
      return {
        code: String(pallet.code),
        zone_code: String(zone?.code ?? String(pallet.code).split('-')[0]),
        no: String(pallet.code).split('-P')[1] ?? '',
        sku_code: sku?.code ?? null,
        sku_name: sku?.name ?? null,
        unit_type: level,
        fill: round2(fill),
        capacity,
        count: counted.length,
        equivalent_pack: eq,
        keq,
        band: level === 'Bal' ? (fill > 0 ? bandOf(palRec, palRec) : 'kosong') : bandOf(keq, palRec),
        capacity_keq: palCap,
        recommended: palRec,
        is_active: pallet.is_active !== false,
        is_full: keq >= palCap,
        units: mine.map(unitRow)
      }
    })

  const zones = readTable('warehouse_zone')
    .filter(zone => zone.zone_type === 'STORAGE_FG')
    .map(zone => {
      const mine = pallets
        .filter(pallet => pallet.zone_code === zone.code)
        .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
      const used = round2(mine.reduce((sum, pallet) => sum + pallet.keq, 0))
      const empty = mine.filter(pallet => !pallet.units.length).length
      return {
        code: String(zone.code),
        name: String(zone.name ?? zone.code),
        pallets: mine,
        keq: used,
        capacity: mine.reduce((sum, pallet) => sum + pallet.capacity_keq, 0),
        equivalent_pack: mine.reduce((sum, pallet) => sum + pallet.equivalent_pack, 0),
        skus: [...new Set(mine.map(pallet => pallet.sku_code).filter((code): code is string => Boolean(code)))],
        state:
          empty === mine.length
            ? 'Kosong'
            : empty === 0 && mine.every(pallet => pallet.keq >= pallet.recommended)
              ? 'Penuh'
              : 'Terisi'
      }
    })
    .sort((a, b) => a.code.localeCompare(b.code))

  const packs = readTable('fg_pack_stock').filter(pack => pack.status === 'IN_STOCK' && num(pack.qty_pack) > 0)
  const containers = readTable('container').map(container => {
    const slops = readTable('fg_label').filter(
      label => idOf(label.container_id) === container.id && FG_PALLET_STATUSES.has(String(label.label_status))
    )
    const mineSlops = slops.length
      ? slops
      : readTable('fg_label').filter(
          label => idOf(label.container_id) === container.id && label.label_status === 'MERGE_RESERVED'
        )
    const minePacks = packs.filter(pack => idOf(pack.container_id) === container.id)
    const groups = new Map<string, { sku_code: string; batch_no: string; unit: string; qty: number }>()
    mineSlops.forEach(label => {
      const sku = products.get(idOf(label.sku_id))?.code ?? ''
      const batch = batches.get(idOf(label.batch_id)) ?? ''
      const key = `${sku}|${batch}|Slop`
      groups.set(key, { sku_code: sku, batch_no: batch, unit: 'Slop', qty: (groups.get(key)?.qty ?? 0) + 1 })
    })
    minePacks.forEach(pack => {
      const sku = products.get(idOf(pack.sku_id))?.code ?? ''
      const batch = batches.get(idOf(pack.batch_id)) ?? ''
      groups.set(`${sku}|${batch}|Pack|${pack.id}`, {
        sku_code: sku,
        batch_no: batch,
        unit: 'Pack',
        qty: num(pack.qty_pack)
      })
    })
    const items = [...groups.values()]
    /** v7.50 U-18: satu baris per label Slop dan per tumpukan pack, masing-masing bisa dibuka detail dan cetak ulang. */
    const units = [
      ...mineSlops.map(label => ({
        label_no: String(label.label_no),
        level: 'Slop' as const,
        sku_code: products.get(idOf(label.sku_id))?.code ?? '',
        batch_no: batches.get(idOf(label.batch_id)) ?? '',
        qty_pack: products.get(idOf(label.sku_id))?.pack.Slop ?? 0,
        status: String(label.label_status),
        is_return: returLabels.has(label.id as string),
        is_repack: false
      })),
      ...minePacks.map(pack => ({
        label_no: String(pack.label_no),
        level: 'Pack' as const,
        sku_code: products.get(idOf(pack.sku_id))?.code ?? '',
        batch_no: batches.get(idOf(pack.batch_id)) ?? '',
        qty_pack: num(pack.qty_pack),
        status: String(pack.status),
        is_return: String(pack.origin) === 'Retur',
        is_repack: String(pack.origin) === 'REPACK'
      }))
    ]
    return {
      code: String(container.code),
      name: String(container.name),
      kind: String(container.container_type),
      slop: items.filter(item => item.unit === 'Slop').reduce((sum, item) => sum + item.qty, 0),
      pack: items.filter(item => item.unit === 'Pack').reduce((sum, item) => sum + item.qty, 0),
      skus: [...new Set(items.map(item => item.sku_code))],
      batches: new Set(items.map(item => item.batch_no)).size,
      items,
      units
    }
  })

  const bands = { kosong: 0, longgar: 0, hampir: 0, rekomendasi: 0, berlebih: 0 }
  pallets.forEach(pallet => {
    bands[pallet.band as keyof typeof bands] += 1
  })
  const hit = (pallet: (typeof pallets)[number]) =>
    (!band || pallet.band === band) &&
    (!q ||
      low(
        `${pallet.code} ${pallet.zone_code} ${pallet.sku_code} ${pallet.sku_name} ${pallet.units.map(unit => `${unit.label_no} ${unit.batch_no}`).join(' ')}`
      ).includes(q))
  const filteredZones = zones
    .map(zone => ({ ...zone, pallets_shown: zone.pallets.filter(hit).map(pallet => pallet.code) }))
    .filter(zone => zone.pallets_shown.length > 0)

  return { bands, zones: filteredZones, containers, pallet_capacity: PAL_CAP, pallet_recommended: PAL_REC }
}
